(() => {
  'use strict';
  function create(config = {}, transport = window.fetch.bind(window)) {
    const keys = ['cap', 'shirt', 'briefs', 'leggings', 'shortsleeve', 'sleeveless', 'menssleeveless'];
    const catalog = config.products || {};
    const ready = config.verified === true && config.currency === 'AUD' && keys.every(key => catalog[key]?.id && catalog[key].variants?.length);
    const root = typeof config.root === 'string' && /^\/(?!\/)/.test(config.root) ? config.root : '/';
    let busy = false;
    let state = null;
    const unavailable = 'Real checkout is not connected. The products, their variants and stock need verification first.';
    function product(key) { return catalog[key] || null; }
    function choices(key, fit) {
      const p = product(key);
      if (!p || (p.audience && p.audience !== fit)) return [];
      return p.variants.filter(v => {
        const constraints = p.optionConstraints || {};
        if (!Object.entries(constraints).every(([name, value]) => (v.options || []).some(o => o.name.toLowerCase() === name.toLowerCase() && String(o.value).toLowerCase() === String(value).toLowerCase()))) return false;
        const gender = (v.options || []).find(o => /^(gender|fit|collection|range)$/i.test(o.name));
        return !gender || ['unisex', fit.toLowerCase()].includes(String(gender.value).toLowerCase());
      });
    }
    function sizes(key, fit) {
      return [...new Set(choices(key, fit).map(v => (v.options || []).find(o => /^size$/i.test(o.name))?.value).filter(Boolean))];
    }
    function resolve(selection) {
      if (!ready) throw new Error(unavailable);
      const candidates = choices(selection.id, selection.fit).filter(v => {
        const size = (v.options || []).find(o => /^size$/i.test(o.name));
        const colour = (v.options || []).find(o => /^(color|colour)$/i.test(o.name));
        const expectedColour = selection.id === 'cap' ? 'black' : 'silver';
        return size?.value === selection.size && (!colour || String(colour.value).toLowerCase() === expectedColour);
      });
      if (candidates.length !== 1) throw new Error('This size and fit does not have one verified matching product variant. Please choose another option.');
      const variant = candidates[0];
      if (!variant.available) throw new Error('This size is currently unavailable. Please choose another size.');
      if (!Number.isFinite(selection.expectedPrice) || Number(variant.price) !== Math.round(Number(selection.expectedPrice) * 100)) throw new Error('The Shopify price differs from the displayed price. Checkout is paused until the price is confirmed.');
      return variant;
    }
    async function request(endpoint, body) {
      const response = await transport(root + endpoint, {
        method: body ? 'POST' : 'GET', credentials: 'same-origin',
        headers: { 'Accept': 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {})
      });
      let result;
      try { result = await response.json(); } catch (_) { throw new Error('Shopify returned an unreadable cart response. Check the cart before trying again.'); }
      if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : 'Shopify could not update the cart. Check availability and try again.');
      return result;
    }
    async function read() {
      if (!ready) throw new Error(unavailable);
      state = await request('cart.js');
      if (state.currency && state.currency !== config.currency) throw new Error('The cart currency has changed. Reload the collection before continuing.');
      return state;
    }
    async function write(action) {
      if (!ready) throw new Error(unavailable);
      if (busy) throw new Error('A cart update is already in progress.');
      busy = true;
      try { return await action(); } finally { busy = false; }
    }
    async function add(selections) {
      return write(async () => {
        const items = selections.map(s => ({ id: resolve(s).id, quantity: Math.max(1, Math.min(9, Math.floor(s.qty || 1))), properties: { '_emf_system': 'emf-collection', '_emf_fit_view': s.fit } }));
        if (!items.length) throw new Error('Choose a garment first.');
        try { await request('cart/add.js', { items }); }
        catch (error) {
          // Shopify can add available quantities before returning an inventory error.
          // Reconcile the server cart; never automatically repeat a potentially executed write.
          try { error.cart = await read(); } catch (_) { /* Preserve the original update error. */ }
          throw error;
        }
        return read();
      });
    }
    async function change(key, quantity) {
      return write(async () => {
        const current = await read();
        if (!current.items?.some(i => i.key === key)) throw new Error('The cart has changed. Reopen it before updating this item.');
        try { state = await request('cart/change.js', { id: key, quantity: Math.max(0, Math.min(9, Math.floor(quantity))) }); }
        catch (error) { try { error.cart = await read(); } catch (_) {} throw error; }
        return state;
      });
    }
    async function checkout() {
      if (!ready) throw new Error(unavailable);
      if (busy) throw new Error('A cart update is still in progress. Please try again when it finishes.');
      const current = await read();
      if (!current.item_count) throw new Error('Your cart is empty. Add a garment first.');
      const form = document.createElement('form');
      form.method = 'post'; form.action = root + 'cart'; form.hidden = true;
      form.target = '_self';
      const button = document.createElement('button'); button.type = 'submit'; button.name = 'checkout'; button.value = 'Checkout';
      form.append(button); document.body.append(form); form.requestSubmit(button);
    }

    return Object.freeze({ ready, product, sizes, resolve, read, add, change, checkout });
  }
  window.EMFShopifyCart = Object.freeze({ create });
})();
